import { Err, Ok, Result } from 'ts-res';
import { Hasher } from 'src/hasher';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { VariationDataType } from 'src/storage';
import { Utilities } from 'src/utilities';
import {
  ConsentBlockingBehaviour,
  FeatureFlagType,
  RuleItemType,
  RuleType,
  VariationByExpositionType,
} from 'src/clientConfiguration/types';
import {
  BaseVariationDataType,
  FeatureFlagVariationType,
  GetVariationParametersType,
  SimulatedVariationJson,
  UpdateDataStatusParametersType,
} from './types';
import {
  FeatureFlagVariationsType,
  ForcedExperimentVariationDataType,
  ForcedFeatureVariationDataType,
  ForcedVariationRuleType,
  ForcedFeatureVariationType,
  ForcedVariationVarByExpType,
  IStorage,
  SimulatedVariationType,
  ForcedExperimentVariationType,
  LegalConsent,
} from 'src/storage/types';
import { IVisitorCodeManager } from 'src/visitorCodeManager';
import { TrackingStatus } from 'src/types';
import { KameleoonLogger } from 'src/logging';
import { ClientConfiguration } from 'src/clientConfiguration';
import { KameleoonVisitorDataType } from 'src/kameleoonData/types';
import { CBScores } from 'src/kameleoonData/cbScores';

interface IVariationConfiguration {
  /**
   * @method getStoredVariations - obtains campaigns with according variations that are *already assigned* and *stored* in the `variationStorage`.
   */
  getStoredVariations(
    visitorCode: string,
  ): Result<FeatureFlagVariationsType, KameleoonError>;
  /**
   * @method updateStoredVariations - updates stored variations for the visitor
   */
  updateStoredVariations: (
    visitorCode: string,
    variationData: FeatureFlagVariationsType,
  ) => void;
  /**
   * @method getVariation - obtains variation for the rule of the feature flag which is both targeted and has a variation, if no such rule was found, returns empty rule with default `variationKey`, given an option `withAssignment` of `true` will also assign a new variation and save it in the storage
   */
  getVariation: (
    params: GetVariationParametersType,
  ) => Result<FeatureFlagVariationType, KameleoonError>;
  /**
   * @method getUnsentData - obtains the list of unsent variations
   */
  getUnsentData: (visitorCode: string) => BaseVariationDataType[];
  /**
   * @method getPendingData - obtains the list of pending variations
   */
  getPendingData: (visitorCode: string) => BaseVariationDataType[];
  /**
   * @method updateDataStatus - updates the status of the variations of given experiment ids with the given status
   */
  updateDataStatus: ({
    visitorCode,
    experimentIdList,
    status,
  }: UpdateDataStatusParametersType) => void;

  /**
   * @method updateForcedExperimentVariation - updates the forced variation for the visitor
   */
  updateForcedExperimentVariation: (
    visitorCode: string,
    experimentId: number,
    forcedExperimentVariation: ForcedExperimentVariationType | null,
  ) => void;

  /**
   * @method updateForcedFeatureVariations - updates the simulated variations for the visitor
   */
  updateForcedFeatureVariations: (
    visitorCode: string,
    forcedFeatureVariations: ForcedFeatureVariationType[],
  ) => void;

  /**
   * @method clearSimulatedVariations - clears the simulated feature variations for the visitor
   */
  clearSimulatedVariations: (visitorCode: string) => void;

  /**
   * @method getForcedFeatureVariation - obtains the forced feature variation for the visitor
   */
  getForcedFeatureVariation: (
    visitorCode: string,
    featureKey: string,
  ) => Result<ForcedFeatureVariationType | null, KameleoonError>;

  /**
   * @method getForcedExperimentVariation - obtains the forced experiment variation for the visitor
   */
  getForcedExperimentVariation: (
    visitorCode: string,
    experimentId: number,
  ) => Result<ForcedExperimentVariationType | null, KameleoonError>;
}

export class VariationConfiguration implements IVariationConfiguration {
  private storage: IStorage<VariationDataType>;
  private storageForcedExperimentVariations: IStorage<ForcedExperimentVariationDataType>;
  private storageForcedFeatureVariations: IStorage<ForcedFeatureVariationDataType>;
  private visitorCodeManager: IVisitorCodeManager;
  private clientConfiguration: ClientConfiguration;

  constructor(
    externalStorage: IStorage<VariationDataType>,
    externalStorageForcedExperimentVariations: IStorage<ForcedExperimentVariationDataType>,
    externalStorageForcedFeatureVariations: IStorage<ForcedFeatureVariationDataType>,
    visitorCodeManager: IVisitorCodeManager,
    clientConfiguration: ClientConfiguration,
  ) {
    this.storage = externalStorage;
    this.storageForcedExperimentVariations =
      externalStorageForcedExperimentVariations;
    this.storageForcedFeatureVariations =
      externalStorageForcedFeatureVariations;
    this.visitorCodeManager = visitorCodeManager;
    this.clientConfiguration = clientConfiguration;
    visitorCodeManager.setVariationConfiguration(this);
  }

  public updateDataStatus({
    visitorCode,
    experimentIdList,
    status,
  }: UpdateDataStatusParametersType): Result<void, KameleoonError> {
    const result = this.storage.read();

    if (!result.ok) {
      return result;
    }

    let updatedVariationData: VariationDataType = result.data;

    if (!updatedVariationData[visitorCode]) {
      updatedVariationData[visitorCode] = {};
    }

    for (const experimentId of experimentIdList) {
      updatedVariationData[visitorCode][experimentId] = {
        ...updatedVariationData[visitorCode][experimentId],
        status,
      };
    }

    return this.storage.write(updatedVariationData);
  }

  public getUnsentData(visitorCode: string): BaseVariationDataType[] {
    return this.getVariationData(
      visitorCode,
      (status) => status === TrackingStatus.Unsent,
    );
  }

  public getVisitorsWithUnsentData(): string[] {
    KameleoonLogger.debug`CALL: VariationConfiguration.unsentDataVisitors()`;
    const result = this.storage.read();

    if (!result.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.unsentDataVisitors() -> (result: ${result})`;
      return [];
    }

    const visitors: string[] = [];
    for (const [visitorCode, variations] of Object.entries(result.data)) {
      if (
        Object.values(variations).some(
          (v) => v.status === TrackingStatus.Unsent,
        )
      ) {
        visitors.push(visitorCode);
      }
    }

    KameleoonLogger.debug`RETURN: VariationConfiguration.unsentDataVisitors() -> (visitors: ${visitors})`;
    return visitors;
  }

  public getPendingData(visitorCode: string): BaseVariationDataType[] {
    return this.getVariationData(
      visitorCode,
      (status) => status === TrackingStatus.Pending,
    );
  }

  public getStoredVariations(
    visitorCode: string,
  ): Result<FeatureFlagVariationsType, KameleoonError> {
    const result = this.storage.read();

    if (!result.ok) {
      return result;
    }

    const featureFlagVariations = result.data[visitorCode];

    if (!featureFlagVariations) {
      return Err(
        new KameleoonError(KameleoonException.StorageRead, visitorCode),
      );
    }

    return Ok(featureFlagVariations);
  }

  public getVariation({
    visitorCode,
    visitorIdentifier,
    featureFlag,
    targetingData,
    packageInfo,
    clientConfiguration,
    dataManager,
    legalConsent,
    track = true,
    withAssignment = false,
  }: GetVariationParametersType): Result<
    FeatureFlagVariationType,
    KameleoonError
  > {
    KameleoonLogger.debug`CALL: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment})`;

    const {
      rules,
      featureKey,
      id: featureFlagId,
      defaultVariationKey,
    } = featureFlag;

    const consent = clientConfiguration.isConsentRequired
      ? legalConsent
      : LegalConsent.Given;

    for (const rule of rules) {
      const {
        segment,
        experimentId,
        id,
        exposition,
        respoolTime,
        variationByExposition,
      } = rule;

      const forcedVariationData = this.getForcedExperimentVariation(
        visitorCode,
        rule.experimentId,
      );
      let forcedVariation = null;
      if (forcedVariationData.ok) {
        forcedVariation = forcedVariationData.data;
        if (forcedVariation && forcedVariation.forceTargeting) {
          KameleoonLogger.info`Visitor ${visitorCode} has been forced targeted for ${rule}`;

          const targetedVariation = this.createAndSaveForcedTargetedVariation(
            visitorCode,
            featureFlag,
            rule,
            forcedVariation,
            track,
            withAssignment,
          );

          KameleoonLogger.debug`RETURN: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment}) -> (targetedVariation: ${targetedVariation})`;
          return Ok(targetedVariation);
        }
      }

      const targetingResult = Utilities.checkTargeting({
        segment,
        visitorCode,
        experimentId,
        targetingData,
        packageInfo,
        clientConfiguration,
        dataManager,
        variationConfiguration: this,
      });

      if (!targetingResult.ok) {
        KameleoonLogger.debug`RETURN: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment}) -> (targetedVariation: ${targetingResult})`;
        return targetingResult;
      }

      if (!targetingResult.data) {
        continue;
      }

      KameleoonLogger.info`Visitor ${visitorCode} has been targeted for ${rule}`;

      if (forcedVariation) {
        const targetedVariation = this.createAndSaveForcedTargetedVariation(
          visitorCode,
          featureFlag,
          rule,
          forcedVariation,
          track,
          withAssignment,
        );
        KameleoonLogger.debug`RETURN: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment}) -> (targetedVariation: ${targetedVariation})`;
        return Ok(targetedVariation);
      }

      // --- Note ---
      // Calculate new variation (and assign it if `withAssignment` is `true`)
      // First the the exposed rule is calculated and then the variation inside of it
      const ruleHash = Hasher.getHashDouble({
        visitorIdentifier,
        respoolTime,
        id: String(id),
      });
      KameleoonLogger.debug`Calculated ruleHash: ${ruleHash} for code: ${visitorIdentifier}`;

      if (ruleHash <= exposition) {
        // Checking if the evaluation is blocked due to the consent policy
        if (
          consent == LegalConsent.NotGiven &&
          rule.type == RuleType.EXPERIMENTATION
        ) {
          const behaviour = clientConfiguration.consentBlockingBehaviour;
          if (behaviour == ConsentBlockingBehaviour.PartiallyBlocked) {
            break;
          }
          return Err(
            new KameleoonError(
              KameleoonException.FeatureFlagEnvironmentDisabled,
              `Evaluation of ${rule} is blocked because consent is not provided for visitor '${visitorCode}'`,
            ),
          );
        }
        // evaluate with CB scores if applicable
        let variation = this.evaluateCBScores(
          rule,
          visitorIdentifier,
          targetingData,
        );

        if (!variation) {
          const variationHash = Hasher.getHashDouble({
            visitorIdentifier,
            respoolTime,
            id: String(experimentId),
          });

          KameleoonLogger.debug`Calculated variationHash: ${variationHash} for code: ${visitorIdentifier}`;

          variation = Utilities.getVariationByHash(
            variationByExposition,
            variationHash,
          );
        }

        if (variation) {
          if (withAssignment) {
            this.updateStoredVariations(visitorCode, {
              [experimentId]: {
                variationId: variation.variationId,
                isTargetedRule: rule.type === RuleType.TARGETED_DELIVERY,
                status: track ? TrackingStatus.Unsent : TrackingStatus.Sent,
              },
            });
          }

          const targetedVariation = {
            variationId: variation.variationId,
            variationKey: variation.variationKey,
            rule,
            featureFlagId,
            featureKey,
            experimentId,
            isTargetedRule: rule.type === RuleType.TARGETED_DELIVERY,
          };
          KameleoonLogger.debug`RETURN: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment}) -> (targetedVariation: ${targetedVariation})`;
          return Ok(targetedVariation);
        }
      }

      if (rule.type === RuleType.TARGETED_DELIVERY) {
        const targetedVariation = {
          featureKey,
          featureFlagId,
          experimentId: null,
          variationKey: defaultVariationKey,
          variationId: null,
          rule: null,
          isTargetedRule: true,
        };
        KameleoonLogger.debug`RETURN: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment}) -> (targetedVariation: ${targetedVariation})`;
        return Ok(targetedVariation);
      }
    }

    const targetedVariation = {
      featureKey,
      featureFlagId,
      experimentId: null,
      variationKey: defaultVariationKey,
      variationId: null,
      rule: null,
      isTargetedRule: false,
    };
    KameleoonLogger.debug`RETURN: VariationConfiguration.getVariation(visitorCode: ${visitorCode}, visitorIdentifier: ${visitorIdentifier}, featureFlag: ${featureFlag}, targetingData: ${targetingData}, packageInfo: ${packageInfo}, clientConfiguration, dataManager, withAssignment: ${withAssignment}) -> (targetedVariation: ${targetedVariation})`;
    return Ok(targetedVariation);
  }

  private createAndSaveForcedTargetedVariation(
    visitorCode: string,
    featureFlag: FeatureFlagType,
    rule: RuleItemType,
    forcedVariation: ForcedExperimentVariationType,
    track: boolean,
    withAssignment: boolean,
  ): FeatureFlagVariationType {
    const targetedVariation = {
      variationKey: forcedVariation.varByExp!.variationKey,
      variationId: forcedVariation.varByExp!.variationId,
      rule,
      featureFlagId: featureFlag.id,
      featureKey: featureFlag.featureKey,
      experimentId: rule.experimentId,
      isTargetedRule: rule.type === RuleType.TARGETED_DELIVERY,
    };
    if (withAssignment) {
      this.updateStoredVariations(visitorCode, {
        [rule.experimentId]: {
          variationId: targetedVariation.variationId,
          isTargetedRule: rule.type === RuleType.TARGETED_DELIVERY,
          status: track ? TrackingStatus.Unsent : TrackingStatus.Sent,
        },
      });
    }
    return targetedVariation;
  }

  public updateStoredVariations(
    visitorCode: string,
    variationData: FeatureFlagVariationsType,
  ): Result<void, KameleoonError> {
    KameleoonLogger.debug`CALL: VariationConfiguration.updateStoredVariations(visitorCode: ${visitorCode}, variationData: ${variationData})`;
    const result = this.storage.read();

    if (!result.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.updateStoredVariations(visitorCode: ${visitorCode}, variationData: ${variationData}) -> (result: ${result})`;
      return result;
    }

    let updatedVariationData: VariationDataType = result.data;

    if (!updatedVariationData[visitorCode]) {
      updatedVariationData[visitorCode] = {};
    }
    for (const experimentId in variationData) {
      updatedVariationData[visitorCode][experimentId] =
        variationData[experimentId];
    }

    const resultWrite = this.storage.write(updatedVariationData);
    KameleoonLogger.debug`RETURN: VariationConfiguration.updateStoredVariations(visitorCode: ${visitorCode}, variationData: ${variationData}) -> (resultWrite: ${resultWrite})`;
    return resultWrite;
  }

  private getVariationData(
    visitorCode: string,
    filter: (status: TrackingStatus) => boolean,
  ): BaseVariationDataType[] {
    const result = this.getStoredVariations(visitorCode);

    if (!result.ok) {
      return [];
    }

    const resultData: BaseVariationDataType[] = [];

    for (const [experimentId, variationData] of Object.entries(result.data)) {
      if (filter(variationData.status)) {
        resultData.push({
          experimentId: Number(experimentId),
          variationId: variationData.variationId,
          isTargetedRule: variationData.isTargetedRule,
        });
      }
    }

    return resultData;
  }

  public updateForcedExperimentVariation(
    visitorCode: string,
    experimentId: number,
    forcedExperimentVariation: ForcedExperimentVariationType | null,
  ): Result<void, KameleoonError> {
    KameleoonLogger.debug`CALL: VariationConfiguration.updateForcedExperimentVariation(visitorCode: ${visitorCode}, experimentId: ${experimentId}, forcedExperimentVariation: ${forcedExperimentVariation})`;
    const result = this.storageForcedExperimentVariations.read();

    if (!result.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.updateForcedExperimentVariation(visitorCode: ${visitorCode}, experimentId: ${experimentId}, forcedExperimentVariation: ${forcedExperimentVariation}) -> (result: ${result})`;
      return result;
    }

    let updatedForcedVariationData: ForcedExperimentVariationDataType =
      result.data;

    if (!updatedForcedVariationData[visitorCode]) {
      updatedForcedVariationData[visitorCode] = {};
    }

    if (forcedExperimentVariation) {
      updatedForcedVariationData[visitorCode][experimentId] =
        forcedExperimentVariation;
    } else {
      delete updatedForcedVariationData[visitorCode][experimentId];
    }

    const resultWrite = this.storageForcedExperimentVariations.write(
      updatedForcedVariationData,
    );
    KameleoonLogger.debug`RETURN: VariationConfiguration.updateForcedExperimentVariation(visitorCode: ${visitorCode}, experimentId: ${experimentId}, forcedExperimentVariation: ${forcedExperimentVariation}) -> (resultWrite: ${resultWrite})`;
    return resultWrite;
  }

  public updateForcedFeatureVariations(
    visitorCode: string,
    forcedFeatureVariations: ForcedFeatureVariationType[],
  ): Result<void, KameleoonError> {
    KameleoonLogger.debug`CALL: VariationConfiguration.updateForcedFeatureVariations(visitorCode: ${visitorCode}, forcedFeatureVariations: ${forcedFeatureVariations})`;
    const result = this.storageForcedFeatureVariations.read();

    if (!result.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.updateForcedFeatureVariations(visitorCode: ${visitorCode}, forcedFeatureVariations: ${forcedFeatureVariations}) -> (result: ${result})`;
      return result;
    }

    let updatedVariationData: ForcedFeatureVariationDataType = result.data;
    if (!updatedVariationData[0]) {
      updatedVariationData[0] = {};
    }
    if (!updatedVariationData[1]) {
      updatedVariationData[1] = {};
    }

    const updatedForcedFeatureVariationData = updatedVariationData[0];
    const updatedSimulatedFeatureVariationData = updatedVariationData[1];

    if (!updatedForcedFeatureVariationData[visitorCode]) {
      updatedForcedFeatureVariationData[visitorCode] = {};
    }
    if (!updatedSimulatedFeatureVariationData[visitorCode]) {
      updatedSimulatedFeatureVariationData[visitorCode] = {};
    }

    forcedFeatureVariations.forEach((forcedFeatureVariation) => {
      const targetData = forcedFeatureVariation.simulated
        ? updatedSimulatedFeatureVariationData[visitorCode]
        : updatedForcedFeatureVariationData[visitorCode];
      targetData[forcedFeatureVariation.featureKey] = forcedFeatureVariation;
    });

    const resultWrite =
      this.storageForcedFeatureVariations.write(updatedVariationData);
    KameleoonLogger.debug`RETURN: VariationConfiguration.updateForcedFeatureVariations(visitorCode: ${visitorCode}, forcedFeatureVariations: ${forcedFeatureVariations}) -> (resultWrite: ${resultWrite})`;
    return resultWrite;
  }

  public clearSimulatedVariations(
    visitorCode: string,
  ): Result<void, KameleoonError> {
    KameleoonLogger.debug`CALL: VariationConfiguration.clearSimulatedVariations(visitorCode: ${visitorCode})`;
    const result = this.storageForcedFeatureVariations.read();

    if (!result.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.clearSimulatedVariations(visitorCode: ${visitorCode}) -> (result: ${result})`;
      return result;
    }
    let resultWrite: Result<void, KameleoonError>;
    const updatedVariationData: ForcedFeatureVariationDataType = result.data;

    if (updatedVariationData?.[1]?.[visitorCode]) {
      delete updatedVariationData[1][visitorCode];
      resultWrite =
        this.storageForcedFeatureVariations.write(updatedVariationData);
    } else {
      resultWrite = Ok();
    }

    KameleoonLogger.debug`RETURN: VariationConfiguration.clearSimulatedVariations(visitorCode: ${visitorCode}) -> (resultWrite: ${resultWrite})`;
    return resultWrite;
  }

  public getForcedFeatureVariation(
    visitorCode: string,
    featureKey: string,
  ): Result<ForcedFeatureVariationType | null, KameleoonError> {
    KameleoonLogger.debug`CALL: VariationConfiguration.getForcedFeatureVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey})`;

    const result = this.storageForcedFeatureVariations.read();
    if (!result.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.getForcedFeatureVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}) -> (simulatedResult: ${result})`;
      return result;
    }

    const simulatedData = result.data[1]?.[visitorCode]?.[featureKey];

    if (simulatedData) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.getForcedFeatureVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}) -> (simulatedData: ${simulatedData})`;
      return Ok(simulatedData);
    }

    const forcedData = result.data[0]?.[visitorCode]?.[featureKey];

    KameleoonLogger.debug`RETURN: VariationConfiguration.getForcedFeatureVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}) -> (forcedData: ${forcedData})`;
    return Ok(forcedData);
  }

  public getForcedExperimentVariation(
    visitorCode: string,
    experimentId: number,
  ): Result<ForcedExperimentVariationType | null, KameleoonError> {
    KameleoonLogger.debug`CALL: VariationConfiguration.getForcedExperimentVariation(visitorCode: ${visitorCode}, experimentId: ${experimentId})`;

    const forcedResult = this.storageForcedExperimentVariations.read();
    if (!forcedResult.ok) {
      KameleoonLogger.debug`RETURN: VariationConfiguration.getForcedExperimentVariation(visitorCode: ${visitorCode}, experimentId: ${experimentId}) -> (forcedResult: ${forcedResult})`;
      return forcedResult;
    }

    const forcedData = forcedResult.data[visitorCode]?.[experimentId];

    KameleoonLogger.debug`RETURN: VariationConfiguration.getForcedExperimentVariation(visitorCode: ${visitorCode}, experimentId: ${experimentId}) -> (forcedExperiment: ${forcedData})`;
    return Ok(forcedData);
  }

  private parseForcedVariationData(
    simulatedVariation: SimulatedVariationType,
    simulated = false,
  ): ForcedFeatureVariationType | null {
    const featureFlag = this.clientConfiguration.featureFlags.get(
      simulatedVariation.featureKey,
    );
    if (!featureFlag) {
      KameleoonLogger.error`Simulated feature flag '${simulatedVariation.featureKey}' not found`;
      return null;
    }
    let forcedVariation = null;
    if (simulatedVariation.experimentId === 0) {
      forcedVariation = {
        featureKey: simulatedVariation.featureKey,
        rule: null,
        varByExp: null,
        simulated: simulated,
      };
      return forcedVariation;
    }
    for (const rule of featureFlag.rules) {
      if (simulatedVariation.experimentId !== rule.experimentId) {
        continue;
      }
      const varByExp = rule.variationByExposition.find(
        (varByExp) => varByExp.variationId === simulatedVariation.variationId,
      );
      if (varByExp) {
        forcedVariation = {
          featureKey: simulatedVariation.featureKey,
          rule: {
            type: rule.type,
            experimentId: rule.experimentId,
          } as ForcedVariationRuleType,
          varByExp: {
            variationKey: varByExp.variationKey,
            variationId: varByExp.variationId,
          } as ForcedVariationVarByExpType,
          simulated: simulated,
        };
        return forcedVariation;
      }
    }
    KameleoonLogger.error`Simulated variation ${simulatedVariation.variationId} not found`;
    return null;
  }

  public simulatedVariationFromJson(
    featureKey: string,
    data: any,
  ): ForcedFeatureVariationType | null {
    KameleoonLogger.debug`CALL: VariationConfiguration.simulatedVariationFromJson(featureKey: ${featureKey}, data: ${data})`;
    if (!this.isSimulatedVariationJson(data)) {
      return null;
    }
    const forcedVariation = this.parseForcedVariationData(
      {
        featureKey,
        variationId: data.varId,
        experimentId: data.expId,
      },
      true,
    );
    KameleoonLogger.debug`RETURN: VariationConfiguration.simulatedVariationFromJson(featureKey: ${featureKey}, data: ${data}) -> (forcedVariation: ${forcedVariation})`;
    return forcedVariation;
  }

  private isSimulatedVariationJson(
    value: any,
  ): value is SimulatedVariationJson {
    return value && (typeof value.expId === 'number' || value.expId === null);
  }

  private evaluateCBScores(
    rule: RuleItemType,
    visitorIdentifier: string,
    targetingData?: KameleoonVisitorDataType,
  ): VariationByExpositionType | null {
    if (!targetingData || !targetingData.cbScores) {
      return null;
    }
    KameleoonLogger.debug`CALL: KameleoonClientImpl.evaluateCBScores(rule: ${rule}, targetingData: ${targetingData}, visitorIdentifier: ${visitorIdentifier})`;

    const experimentId = rule.experimentId;
    const varIdGroupByScores = new CBScores(targetingData.cbScores.cbs)
      .getValues()
      .get(experimentId);

    let variation: VariationByExpositionType | null = null;
    if (varIdGroupByScores !== undefined) {
      const varByExps = rule.variationByExposition;

      const varByExpExistInCBS = varIdGroupByScores
        .map((varGroup) =>
          varByExps.filter((varByExp) =>
            varGroup.ids.includes(varByExp.variationId),
          ),
        )
        .find((varByExpExistInCBS) => varByExpExistInCBS.length > 0);
      if (varByExpExistInCBS) {
        let idx = 0;
        if (varByExpExistInCBS.length > 1) {
          const variationHash = Hasher.getHashDouble({
            visitorIdentifier,
            respoolTime: rule.respoolTime,
            id: String(experimentId),
          });
          KameleoonLogger.debug`Calculated cbs hash ${variationHash} for code ${visitorIdentifier}`;
          idx = Math.min(
            Math.floor(variationHash * varByExpExistInCBS.length),
            varByExpExistInCBS.length - 1,
          );
        }
        variation = varByExpExistInCBS[idx];
      }
    }

    KameleoonLogger.debug`RETURN: KameleoonClientImpl.evaluateCBScores(rule: ${rule}, targetingData: ${targetingData}, visitorIdentifier: ${visitorIdentifier}) -> (variation: ${variation})`;
    return variation;
  }
}
